// The VIP helper behind My Mods: of the classes you still need this season, which has an edge
// on the champion you'd face? The edge is elemental, read from the data the unit cards use.
// A kit's side comes from what it deals: a human class's own skillset (each ability weighted
// by its roll chance) or a monster's intrinsic skills, each ability's "Element:", and whether
// its damage is Faith magic. A champion unit's side comes from what it takes double from or
// shrugs off: gear "Absorb / Half / Weak / Cancel" clauses, and the class's own "Effect:"
// line in its tip (Treant "Weak Fire", Steel Giant "Null Holy, Dark … Permanent Innocent").
// It's a heuristic and says why; the battle sim, on demand in the modal, is the reality check.
// Pure and data-injected, so it runs in node tests.

import { parseItemElements } from './elements.js';

// A damaging ability of an element counts once a unit has at least this chance of having one.
export const MIN_P = 0.5;
// Geomancy only works on matching terrain ("Terrain: Dirt, Wasteland, …"), and the champion
// match's map isn't known when you enter, so a terrain-bound ability counts at this chance.
export const TERRAIN_FACTOR = 0.5;
// Win-rate points one champion unit's weakness (or immunity) is worth when ranking classes.
export const EDGE_WEIGHT = 3;

// One ability: its element, whether it deals damage, and whether that damage is Faith
// magic (Faith 0, i.e. Innocent, nullifies it; flat-MA damage like Flame Attack still lands).
export function abilityTraits(info) {
  const s = String(info || '');
  const effect = (s.match(/Effect:\s*(.*)$/) || [])[1] || '';
  return {
    element: (s.match(/Element:\s*([A-Za-z]+)/) || [])[1] || '',
    damage: /\bDamage\b/.test(effect),
    faith: /Faith\(/.test(effect),
    terrain: /\bTerrain:/.test(s),
  };
}

// What a kit deals. abilities = [{ name, chance }] (chance 1 for a monster's own skills).
// elements[E] = { p: chance of at least one damaging E ability, faith: all of them are Faith
// magic, names }. dmg / faithDmg = expected damaging abilities, and how many are Faith magic.
// main = the element most of its damage carries, when one carries at least half.
export function kitProfile(abilities, abilityInfo) {
  const acc = {};
  let dmg = 0, faithDmg = 0;
  for (const { name, chance } of abilities) {
    const t = abilityTraits(abilityInfo[name]);
    if (!t.damage) continue;
    const c = Math.max(0, Math.min(1, chance)) * (t.terrain ? TERRAIN_FACTOR : 1);
    dmg += c;
    if (t.faith) faithDmg += c;
    if (!t.element) continue;
    const e = acc[t.element] || (acc[t.element] = { miss: 1, faith: true, names: [], w: 0 });
    e.miss *= 1 - c;
    e.faith = e.faith && t.faith;
    e.w += c;
    e.names.push(name);
  }
  return finishKit(acc, dmg, faithDmg);
}

function finishKit(acc, dmg, faithDmg) {
  const elements = {};
  let main = '', mainW = 0;
  for (const [el, e] of Object.entries(acc)) {
    elements[el] = { p: 1 - e.miss, faith: e.faith, names: e.names, w: e.w, miss: e.miss };
    if (e.w > mainW) { main = el; mainW = e.w; }
  }
  return { elements, dmg, faithDmg, main: dmg > 0 && mainW >= dmg / 2 ? main : '' };
}

// A class plus a forced secondary skillset: both kits at once.
export function mergeKits(a, b) {
  const acc = {};
  for (const k of [a, b]) {
    for (const [el, e] of Object.entries(k.elements)) {
      const m = acc[el] || (acc[el] = { miss: 1, faith: true, names: [], w: 0 });
      m.miss *= e.miss;
      m.faith = m.faith && e.faith;
      m.w += e.w;
      m.names.push(...e.names);
    }
  }
  return finishKit(acc, a.dmg + b.dmg, a.faithDmg + b.faithDmg);
}

// A champion unit's elemental defenses, from its gear and its class tip. itemInfo maps an
// item name to its info text; tip is the class's tip text (monster innates live in it).
// "Null X" in a tip means what "Cancel X" means on gear. innocent = Permanent/Initial
// Innocent (Faith magic can't touch it); float = Levitate or Permanent/Initial Float
// (Earth can't touch it).
export function unitDefenses(unit, itemInfo, tip) {
  const sets = { weak: new Set(), half: new Set(), absorb: new Set(), cancel: new Set() };
  const statuses = new Set();
  const take = (text) => {
    const effect = (String(text || '').match(/Effect:\s*(.*)$/) || [])[1];
    if (!effect) return;
    const p = parseItemElements('Effect: ' + effect.replace(/\bNull\b/g, 'Cancel'));
    for (const k of Object.keys(sets)) for (const e of p[k]) sets[k].add(e);
    for (const clause of effect.split(';')) {
      const m = clause.trim().match(/^(?:Permanent|Initial)\s+(.*)$/);
      if (m) for (const s of m[1].split(/,\s*/)) statuses.add(s.trim().replace(/\.$/, ''));
    }
  };
  for (const slot of ['Mainhand', 'Offhand', 'Head', 'Armor', 'Accessory']) take(itemInfo[unit[slot]]);
  take(tip);
  const innates = (String(tip || '').match(/Innate:\s*([^.]*)\./) || [])[1] || '';
  return {
    name: unit.Name, cls: unit.Class, ...sets,
    innocent: statuses.has('Innocent'),
    float: statuses.has('Float') || /\bLevitate\b/.test(innates),
  };
}

// How a kit fares against a champion team. A hit is a unit it can strike where that unit is
// weak (and not also halving, absorbing or nulling it). A block is a unit that shrugs off the
// kit's main element, or its Faith magic when it's mostly a caster. score = hits − blocks.
export function kitEdge(kit, defenders) {
  const hits = [], blocks = [];
  for (const d of defenders) {
    let hit = null;
    for (const [el, k] of Object.entries(kit.elements)) {
      if (k.p < MIN_P || !d.weak.has(el) || d.half.has(el) || d.absorb.has(el) || d.cancel.has(el)) continue;
      if ((k.faith && d.innocent) || (el === 'Earth' && d.float)) continue;
      if (!hit || k.p > hit.p) hit = { unit: d.name, cls: d.cls, element: el, p: k.p };
    }
    if (hit) { hits.push(hit); continue; }
    if (kit.main && (d.absorb.has(kit.main) || d.cancel.has(kit.main))) {
      blocks.push({ unit: d.name, cls: d.cls, why: d.absorb.has(kit.main) ? 'absorb' : 'null', element: kit.main });
    } else if (d.innocent && kit.dmg > 0 && kit.faithDmg / kit.dmg >= 0.6) {
      blocks.push({ unit: d.name, cls: d.cls, why: 'innocent' });
    }
  }
  return { hits, blocks, score: hits.length - blocks.length };
}

// Rank the classes you could enter as for the VIP Quest.
//   needed      class names you still need this season
//   isMonster   (cls) → true for a monster class
//   gated       (cls) → true for a strong or elite monster. Those share a cooldown, so an
//               owned one you still need is the scarcest chance there is: they rank first,
//               ahead of everything else, and the score orders them among themselves.
//   owned       Set of monster class names you own (a monster entry needs the monster)
//   kitOf       (cls) → kitProfile of the class's own kit
//   strength    (cls) → its win rate (%), the base of the score
//   defenders   unitDefenses of the champion's units
//   skillsets   owned forceable skillsets: [{ token, name, kit, rank(cls) }]; a human can
//               force one as a secondary to bring an element the champion is weak to
//   ownSkillset (cls) → the skillset name a class already has (so it isn't forced twice)
// Returns rows best-first: { cls, kind, gated, base, edge, skill, score }. skill is the
// skillset to force ({ token, name, gain }) or null.
export function vipCandidates({ needed, isMonster, gated = () => false, owned, kitOf, strength, defenders, skillsets = [], ownSkillset = () => '', edgeWeight = EDGE_WEIGHT }) {
  const rows = [];
  for (const cls of needed) {
    const monster = isMonster(cls);
    if (monster && !owned.has(cls)) continue;
    const kit = kitOf(cls);
    let edge = kitEdge(kit, defenders), skill = null;
    if (!monster) {
      // The owned skillset that adds the most edge; ties go to the one that does best on the class.
      let best = null;
      for (const s of skillsets) {
        if (s.name === ownSkillset(cls)) continue;
        const e = kitEdge(mergeKits(kit, s.kit), defenders);
        const gain = e.score - edge.score;
        if (gain <= 0) continue;
        const rank = s.rank(cls);
        if (!best || gain > best.gain || (gain === best.gain && rank > best.rank)) best = { token: s.token, name: s.name, gain, rank, edge: e };
      }
      if (best) { edge = best.edge; skill = { token: best.token, name: best.name, gain: best.gain }; }
    }
    const base = strength(cls) ?? 50;
    rows.push({ cls, kind: monster ? 'monster' : 'class', gated: monster && gated(cls), base, edge, skill, score: base + edgeWeight * edge.score });
  }
  return rows.sort((a, b) => b.gated - a.gated || b.score - a.score || b.base - a.base || a.cls.localeCompare(b.cls));
}
